import _ from "lodash";
import { successErrorType } from "../cache/cache";
import { IRole, IUserDuelAccess } from "../types/types";
import { Maybe, Permissions, User } from "../types/graphql";
import i18n, { SupportedLanguage } from "../i18n";

export const setError = (errors: successErrorType[], error: successErrorType[]): successErrorType[] => {
    return _.uniqWith([...errors, ...error], _.isEqual);
};

export const validateEmail = (email: string) => {
    const re =
        /^(([^<>()\[\]\\.,;:$\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/; // eslint-disable-line
    return re.test(String(email).toLowerCase());
};

export const convertFileToBase64 = (file: File) => {
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
    });
};

export const truncate = (str, len = 150) => {
    return str.length > len ? `${str.substring(0, len)}...` : str;
};

export const validURL = (str) => {
    var pattern = new RegExp(
        "^(https?:\\/\\/)?" + // protocol
            "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // domain name
            "((\\d{1,3}\\.){3}\\d{1,3}))" + // OR ip (v4) address
            "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // port and path
            "(\\?[;&a-z\\d%_.~+=-]*)?" + // query string
            "(\\#[-a-z\\d_]*)?$",
        "i",
    ); // fragment locator
    return !!pattern.test(str);
};

export const validateYouTubeStreamKeyFormat = (key: string): boolean => {
    const pattern = /^[0-9a-z]{4}-[0-9a-z]{4}-[0-9a-z]{4}-[0-9a-z]{4}-[0-9a-z]{4}$/;
    return pattern.test(key);
};

/**
 * Escape special characters in the given string of text.
 *
 * @param  {string} string The string to escape for inserting into HTML
 * @return {string}
 * @public
 */

export const escapeHtml = (string) => {
    var str = "" + string;
    var match = /["'&<>]/.exec(str);

    if (!match) {
        return str;
    }

    var escape;
    var html = "";
    var index = 0;
    var lastIndex = 0;

    for (index = match.index; index < str.length; index++) {
        switch (str.charCodeAt(index)) {
            case 34: // "
                escape = "&quot;";
                break;
            case 38: // &
                escape = "&amp;";
                break;
            case 39: // '
                escape = "&#39;";
                break;
            case 60: // <
                escape = "&lt;";
                break;
            case 62: // >
                escape = "&gt;";
                break;
            default:
                continue;
        }

        if (lastIndex !== index) {
            html += str.substring(lastIndex, index);
        }

        lastIndex = index + 1;
        html += escape;
    }

    return lastIndex !== index ? html + str.substring(lastIndex, index) : html;
};

/**
 * Склонение числительных
 * countTextFormatter(count, ['найдена', 'найдено', 'найдены'])
 * @param n
 * @param titles
 * @returns
 */
export const countTextFormatter = (n: number, titles: [string, string, string]) => {
    return titles[
        n % 10 === 1 && n % 100 !== 11 ? 0 : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 1 : 2
    ];
};

export const getAccessToken = () => {
    return process.env.REACT_APP_STAGE === "production"
        ? localStorage.getItem("AccessToken")
        : sessionStorage.getItem("AccessToken");
};

export const setAccessToken = (token: string) => {
    process.env.REACT_APP_STAGE === "production"
        ? localStorage.setItem("AccessToken", token)
        : sessionStorage.setItem("AccessToken", token);
};

export const deleteCookie = (name: string) => {
    localStorage.removeItem("refreshAuthToken");
    sessionStorage.removeItem("refreshAuthToken");
};

export const getCookieValue = (key: string) => {
    return process.env.REACT_APP_STAGE === "production"
        ? localStorage.getItem("refreshAuthToken")
        : sessionStorage.getItem("refreshAuthToken");
};

export const setRefreshCookie = (value: string) => {
    process.env.REACT_APP_STAGE === "production"
        ? localStorage.setItem("refreshAuthToken", value)
        : sessionStorage.setItem("refreshAuthToken", value);
};

export const removeAccessToken = () => {
    localStorage.removeItem("AccessToken");
    sessionStorage.removeItem("AccessToken");
};

export const getLastMessageId = () => {
    return process.env.REACT_APP_STAGE === "production"
        ? localStorage.getItem("LastMessageId")
        : sessionStorage.getItem("LastMessageId");
};

export const setLastMessageId = (lastMessageId: string) => {
    process.env.REACT_APP_STAGE === "production"
        ? localStorage.setItem("LastMessageId", lastMessageId)
        : sessionStorage.setItem("LastMessageId", lastMessageId);
};

export const getWeekDay = (num: number | null | undefined) => {
    if (num === undefined || num === null || ![0, 1, 2, 3, 4, 5, 6].includes(num)) {
        return null;
    }
    return i18n.t(`components.periods.periodItem.weekDays.${num}`);
};

export function capitalizeFLetter(str: string): string {
    return str[0].toUpperCase() + str.slice(1);
}

export function getDuelAccess(duelAccess: IUserDuelAccess | string, duelPrice: number): string {
    switch (duelAccess) {
        case "fee":
            return i18n.t("components.duelAccess.types.fee", { price: duelPrice });
        case "training":
            return i18n.t("components.duelAccess.types.training");
        case "membership":
            return i18n.t("components.duelAccess.types.membership");
        case "free":
            return i18n.t("components.duelAccess.types.bonusDuel");
        case "free-role":
            return i18n.t("components.duelAccess.types.freeRole");
        default:
            return i18n.t("components.duelAccess.types.feeDefault");
    }
}

export function getFreeRoles(permissions: Maybe<Permissions>): string[] {
    let result: IRole[] = ["judge1", "judge2", "judge3", "master", "player1", "player2"];
    if (!permissions) {
        return [];
    }
    if (permissions?.participateFree) {
        return result;
    }
    if (!permissions?.participateFreeJudge) {
        result = result.filter((role) => !role.includes("judge"));
    }
    if (!permissions?.participateFreeMaster) {
        result = result.filter((role) => role !== "master");
    }
    if (!permissions?.participateFreePlayer) {
        result = result.filter((role) => !role.includes("player"));
    }
    return getFormattedUserFreeRoles(result);
}

const ALL_ROLE_KEYS = ["judge", "player", "master"] as const;
type RoleGroupKey = (typeof ALL_ROLE_KEYS)[number];

function getRoleGroupKey(role: IRole): RoleGroupKey {
    if (role.includes("judge")) {
        return "judge";
    }
    if (role.includes("player")) {
        return "player";
    }
    return "master";
}

export function getDuelPayments(
    duelAccess: IUserDuelAccess,
    balance: number,
    freeRoles: IRole[],
    price?: number,
): { access: string; freeRoles: string[]; rolesWithAccess: string[] } {
    let access = "";
    switch (duelAccess) {
        case "fee":
            access = price
                ? i18n.t("components.duelAccess.payment.fee", { price })
                : i18n.t("components.duelAccess.payment.feeBalance", { balance });
            break;
        case "training":
            access = i18n.t("components.duelAccess.payment.training");
            break;
        case "membership":
            access = i18n.t("components.duelAccess.payment.membership");
            break;
        case "free":
            access = i18n.t("components.duelAccess.payment.bonusDuel");
            break;
        default:
            access = i18n.t("components.duelAccess.payment.default");
    }

    const freeGroupKeys = [...new Set(freeRoles.map(getRoleGroupKey))];
    const rolesWithAccessKeys = ALL_ROLE_KEYS.filter((key) => !freeGroupKeys.includes(key));

    return {
        access,
        freeRoles: freeGroupKeys.map((key) => i18n.t(`common.roles.${key}`)),
        rolesWithAccess: rolesWithAccessKeys.map((key) => i18n.t(`common.roles.${key}`)),
    };
}

export function getFormattedUserFreeRoles(freeRoles: IRole[]): string[] {
    return [...new Set(freeRoles.map(getRoleGroupKey))].map((key) => i18n.t(`common.roles.${key}`));
}

export function hasUserDuelAccess(user: User | null | undefined): boolean {
    const freeRolesPermission =
        !!user?.permissions?.participateFree &&
        (!!user?.permissions?.participateFreeJudge ||
            !!user?.permissions?.participateFreeMaster ||
            !!user?.permissions?.participateFreePlayer);

    const hasAccess =
        (user?.duelAccess &&
            ((user.duelAccess === "fee" && user?.balance > 0) ||
                ["training", "membership", "free"].includes(user?.duelAccess || ""))) ||
        freeRolesPermission;

    return hasAccess;
}

export const getLocalizedText = (
    value: { ru?: string | null; en?: string | null; et?: string | null; es?: string | null } | null | undefined,
    language: SupportedLanguage,
): string => {
    return value?.[language] || value?.en || value?.ru || value?.et || value?.es || "";
};
